import axios from 'axios';
import addParamsToUrls from '../../../../helpers/addParametersToURLs';
import storageAvailable from '../../../../helpers/storage_available';
import { trackPaymentFailure } from '../../tracking/event';
import {
  FullPaymentPayload,
  PaymentDetails,
  ReturnUrls,
  PaymentErrorObject,
} from '../CheckoutTypes';

function addParamsToUrl(url, params) {
  const addParams = addParamsToUrls(url);
  return addParams(params);
}

function postToPaymentApi({
  payload,
  baseUrl,
  returnUrls,
  callback,
}: {
  payload: FullPaymentPayload;
  baseUrl: string;
  returnUrls: ReturnUrls;
  callback: (transactionId: string) => void;
}) {
  const currentUrl = window.location.href;
  const params = {} as any;
  return axios
    .post(`${baseUrl}/api/1.0/payments`, payload)
    .then((response) => {
      callback(response.data.data.external_transaction.id);
      if (returnUrls.success) {
        params.transactionId = response.data.data.external_transaction.id;
        const returnUrl = addParamsToUrl(returnUrls.success, params);
        return window.location.assign(returnUrl);
      }
      return true;
    })
    .catch((error) => {
      if (error.response && error.response.status == 400) {
        const { errors } = error.response.data;
        const paymentAction = errors.find((err) => err.code === 3005);
        const subscriptionAction = errors.find((err) => err.code === 3007);
        const message =
          paymentAction && paymentAction.message ? paymentAction.message : '';
        if (paymentAction) {
          return <PaymentErrorObject>{
            actionRequired: true,
            subscription: false,
            client_secret: paymentAction.data.client_secret,
            message: message,
            paymentProvider: payload.external_transaction.service.type,
          };
        }
        if (subscriptionAction) {
          return <PaymentErrorObject>{
            actionRequired: true,
            subscription: true,
            client_secret: subscriptionAction.data.client_secret,
            invoice: subscriptionAction.data.invoice,
            message: message,
            paymentProvider: payload.external_transaction.service.type,
          };
        }
      }
      const url = addParamsToUrl(
        error.response && error.response.status == 400
          ? returnUrls.failure
          : returnUrls.error,
        params,
      );
      const timeStamp = Date.now();
      localStorage.setItem(
        'failed_purchase',
        JSON.stringify({
          timestamp: timeStamp,
          products: payload.products,
        }),
      );
      trackPaymentFailure({
        productsArray: payload.products,
        paymentProvider: payload.external_transaction.service.type,
        paymentProviderSubType: payload.external_transaction.service.subtype,
        timeStamp,
      });

      window.addEventListener('hashchange', function(event) {
        // if only the hash changes on assign,
        // then the browser won't navigate to a new page,
        // so we have to manually reload
        window.location.reload();
      });

      if (currentUrl === url) {
        window.location.reload();
      } else {
        window.location.assign(url);
      }
      return false;
    });
}

function createPayload({
  affiliateId,
  currency,
  products,
  paymentProvider,
  paymentProviderSubType,
  paymentDomain,
  platform,
  userId,
  context = '',
  amount,
  token,
  invoice,
  upgradeContext,
  promo_code,
}: PaymentDetails): FullPaymentPayload {
  const payload = <FullPaymentPayload>{
    charge: {
      currency,
      amount: parseInt((parseFloat(amount) * 100).toFixed(0), 10),
    },
    external_transaction: {
      service: {
        type: paymentProvider,
      },
    },
    source: {
      domain: paymentDomain,
      platform: platform,
    },
    user: {
      id: userId,
    },
    products,
  };
  if (paymentProvider == 'paypal') {
    payload.order_id = token;
  } else {
    payload.token = token;
  }
  if (context) {
    payload.context = context;
  }
  if (affiliateId) {
    payload.affiliate = { id: affiliateId };
  }
  if (invoice) {
    payload.invoice = invoice;
  }
  if (upgradeContext) {
    payload.upgradeContext = upgradeContext;
  }
  if (paymentProviderSubType) {
    payload.external_transaction.service.subtype = paymentProviderSubType;
  }
  if (promo_code) {
    payload.coupon = { promo_code: promo_code };
  }
  return payload;
}

export default function makePurchase({
  paymentDetails,
  baseUrl,
  returnUrls,
  callback,
}: {
  paymentDetails: PaymentDetails;
  baseUrl: string;
  returnUrls: ReturnUrls;
  callback: () => void;
}) {
  const payload = createPayload(paymentDetails);
  return postToPaymentApi({
    payload,
    baseUrl,
    returnUrls,
    callback,
  });
}
